import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Paperclip, Loader2, X, ChevronLeft, ChevronRight, Calendar, Image as ImageIcon,
  Stethoscope, Activity, Flame, FlaskConical, Syringe, Filter, GitCompare,
} from 'lucide-react';
import { toast } from 'sonner';
import PhotoCompareDialog from '../components/PhotoCompareDialog';
import { formatLocalDate } from '../utils/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  appointment: { label: 'Appointment', icon: Stethoscope, color: 'text-blue-700 bg-blue-100', dot: 'bg-blue-500' },
  symptom: { label: 'Symptom', icon: Activity, color: 'text-yellow-700 bg-yellow-100', dot: 'bg-yellow-500' },
  flare: { label: 'Flare', icon: Flame, color: 'text-orange-700 bg-orange-100', dot: 'bg-orange-500' },
  lab: { label: 'Lab', icon: FlaskConical, color: 'text-cyan-700 bg-cyan-100', dot: 'bg-cyan-500' },
  vaccine: { label: 'Vaccine', icon: Syringe, color: 'text-teal-700 bg-teal-100', dot: 'bg-teal-500' },
};

const PARENT_ROUTES = {
  appointment: '/appointments',
  symptom: '/tracker',
  flare: '/flare-journal',
  lab: '/lab-tracker',
  vaccine: '/vaccines',
};

const MyPhotosPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [blobs, setBlobs] = useState({}); // id -> object URL
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [lightbox, setLightbox] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter !== 'all') params.set('parent_type', typeFilter);
      if (fromDate) params.set('from_date', fromDate);
      if (toDate) params.set('to_date', toDate);
      const res = await axios.get(`${API}/api/attachments/all?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data.attachments || []);
    } catch (e) {
      toast.error('Failed to load photos');
    } finally {
      setLoading(false);
    }
  }, [token, typeFilter, fromDate, toDate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Lazy load thumbnails as items change
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const next = { ...blobs };
      for (const att of items) {
        if (next[att.id]) continue;
        try {
          const r = await fetch(`${API}/api/attachments/${att.id}/download?auth=${encodeURIComponent(token)}`);
          if (!r.ok) continue;
          const b = await r.blob();
          if (cancelled) return;
          next[att.id] = URL.createObjectURL(b);
        } catch (e) { /* skip */ }
      }
      if (!cancelled) setBlobs(next);
    };
    if (items.length > 0) load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useEffect(() => {
    return () => {
      Object.values(blobs).forEach(URL.revokeObjectURL);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const counts = useMemo(() => {
    const c = { all: items.length };
    items.forEach(i => { c[i.parent_type] = (c[i.parent_type] || 0) + 1; });
    return c;
  }, [items]);

  const fmtDate = (s) => s ? formatLocalDate(s, undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  return (
    <div className="space-y-6" data-testid="my-photos-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-100 rounded-xl p-6 md:p-8">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-sm">
            <ImageIcon className="w-8 h-8 text-orange-600" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-nunito font-bold text-[#2D2D2D] mb-2">
              My Photos
            </h1>
            <p className="text-[#57534E] max-w-2xl">
              Every photo you've attached across appointments, symptoms, flares, labs and vaccines —
              in one chronological view. Spot visual progression and quickly jump back to the
              source record.
            </p>
          </div>
        </div>
      </div>

      {/* Type filter pills + Compare action */}
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex flex-wrap gap-2" data-testid="type-filter">
        {['all', 'appointment', 'symptom', 'flare', 'lab', 'vaccine'].map(t => {
          const meta = t === 'all' ? null : TYPE_META[t];
          const Icon = meta?.icon;
          const active = typeFilter === t;
          const n = counts[t] || 0;
          return (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 border-2 transition-all ${
                active
                  ? (t === 'all' ? 'bg-primary text-white border-primary' : `${meta.color} border-current`)
                  : 'bg-stone-100 text-stone-500 border-transparent hover:bg-stone-200'
              }`}
              data-testid={`type-filter-${t}`}
            >
              {Icon && <Icon className="w-3.5 h-3.5" />}
              {t === 'all' ? 'All' : meta.label}
              <span className="opacity-70 text-xs">({n})</span>
            </button>
          );
        })}
        </div>
        <Button
          variant="outline"
          onClick={() => setCompareOpen(true)}
          disabled={items.length < 2}
          data-testid="compare-across-categories-btn"
        >
          <GitCompare className="w-4 h-4 mr-2" />
          Compare Across Categories
        </Button>
      </div>

      {/* Date filter */}
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <Filter className="w-4 h-4 text-stone-500 mb-3" />
          <div>
            <Label className="text-xs text-stone-500">From</Label>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-44"
              data-testid="from-date"
            />
          </div>
          <div>
            <Label className="text-xs text-stone-500">To</Label>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-44"
              data-testid="to-date"
            />
          </div>
          {(fromDate || toDate) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setFromDate(''); setToDate(''); }}
            >
              Clear dates
            </Button>
          )}
        </div>
      </Card>

      {/* Gallery */}
      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center">
          <Paperclip className="w-12 h-12 text-stone-300 mx-auto mb-4" />
          <p className="text-[#57534E] font-medium">No photos yet</p>
          <p className="text-sm text-stone-500 mt-1">
            Attach photos from any tracker (appointments, symptoms, flares, labs, vaccines) and they'll appear here.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {items.map((att, idx) => {
            const meta = TYPE_META[att.parent_type];
            const Icon = meta?.icon;
            return (
              <button
                key={att.id}
                onClick={() => setLightbox(idx)}
                className="group relative aspect-square rounded-lg overflow-hidden bg-stone-100 border border-stone-200 hover:ring-2 hover:ring-primary hover:ring-offset-2 transition-all text-left"
                data-testid={`photo-${att.id}`}
              >
                {blobs[att.id] ? (
                  <img
                    src={blobs[att.id]}
                    alt={att.original_filename}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="w-6 h-6 text-stone-400" />
                  </div>
                )}
                {/* Type badge */}
                <div className={`absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold flex items-center gap-1 ${meta?.color || 'bg-white/90'}`}>
                  {Icon && <Icon className="w-2.5 h-2.5" />}
                  {meta?.label || att.parent_type}
                </div>
                {/* Date footer overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white px-2 py-1.5">
                  <p className="text-[10px] font-medium truncate">{att.record_label || att.original_filename}</p>
                  <p className="text-[10px] opacity-80">{fmtDate(att.record_date || att.created_at)}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Lightbox */}
      {lightbox !== null && items[lightbox] && (
        <div
          className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-full"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            data-testid="lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          {lightbox > 0 && (
            <button
              className="absolute left-4 p-2 text-white hover:bg-white/10 rounded-full"
              onClick={(e) => { e.stopPropagation(); setLightbox(lightbox - 1); }}
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
          )}
          {lightbox < items.length - 1 && (
            <button
              className="absolute right-4 p-2 text-white hover:bg-white/10 rounded-full"
              onClick={(e) => { e.stopPropagation(); setLightbox(lightbox + 1); }}
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          )}
          <img
            src={blobs[items[lightbox].id]}
            alt={items[lightbox].original_filename}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 left-4 right-4 flex flex-wrap items-center justify-center gap-3 text-white text-sm" onClick={(e) => e.stopPropagation()}>
            <div className="bg-black/60 px-3 py-1.5 rounded-lg">
              <span className="font-semibold">{items[lightbox].record_label || items[lightbox].original_filename}</span>
              <span className="opacity-70 mx-2">·</span>
              <span>{TYPE_META[items[lightbox].parent_type]?.label}</span>
              <span className="opacity-70 mx-2">·</span>
              <Calendar className="w-3.5 h-3.5 inline mr-1" />
              {fmtDate(items[lightbox].record_date || items[lightbox].created_at)}
            </div>
            <Button
              size="sm"
              variant="outline"
              className="bg-white/10 text-white border-white/30 hover:bg-white/20"
              onClick={() => navigate(PARENT_ROUTES[items[lightbox].parent_type] || '/')}
              data-testid="goto-record-btn"
            >
              Go to record →
            </Button>
          </div>
        </div>
      )}

      {/* Cross-category compare */}
      <PhotoCompareDialog
        open={compareOpen}
        onOpenChange={setCompareOpen}
        token={token}
        parentType={null}
        crossType={true}
        title="Compare Across Categories"
        description="Pick any two photos — even across appointment, symptom, flare, lab, or vaccine — and see them side by side."
      />
    </div>
  );
};

export default MyPhotosPage;
